/**
 * E5.4 step 1 (ARCHITECTURE §5.17; lane F): the planet's forest as a pure function of the planet's seed and a cell. Every
 * cell's stands and trees come from its own seeded streams, so any one cell can be made alone, in any order — by the
 * sim's held band (step 4) and the renderer's far field (step 2) alike — and the same tree always has the same id.
 *
 * - **Cells**: core/cells.ts's cube-sphere at `forestCellsPerEdge` (about FOREST.cellPeople across).
 * - **The biome field** (decision 151: a planet's own kinds, concentrated by place): each of the planet's biomes is a
 *   smooth noise over the sphere about FOREST.regionPeople across, weighted by `biomesFor`; the strongest wins where it
 *   clears FOREST.openBelow, else the ground is open. A pure function of the seed and the direction.
 * - **Stands**: a cell draws FOREST.anchorsPerCell candidate anchors (stream `…|a`). A candidate is a stand where the
 *   field grows a biome, it roots (STAND_ANCHOR_MAX_SLOPE), it is clear of the caller's keep-out, and no candidate that
 *   outranks it (a hash of its cell and slot) and stands within the two radii and a gap is a stand — a greedy independent
 *   set, packed as dressBattleSite packs stands. It reads only the candidates round it, whatever order cells are asked in.
 * - **Trees**: each stand laid out as `dressBattleSite` lays one (its biome's shapes, count, spacing; stream `…|<k>`),
 *   every draw made before its slope test so a tree on a cliff leaves the others where they were; a tree crowding one of
 *   an outranking stand gives way. Id = FOREST.idBase + cell × FOREST.perCell + k × 64 + i — stable whatever drops.
 * - **Measured** (three Lab planets, Oct 11): 43–73 % of the ground in woods; a sonora megaplanet ~110,000 trees in all
 *   (0.004 a square person; the woods at about a third of the Lab site's packing), a rough tephra ~20,000 (its anchors
 *   too steep to root); 0.02–0.09 ms a cell.
 *
 * The terrain is read as passed (slopes, a biome's low or high ground): the caller decides which planet that is.
 * Presentation and sim read the same function. dmath only (through biomes.ts's helpers); no shared stream, no camera.
 */
import type { BiomeId, FloraState, GameState, PlanetState, Vec3 } from '../types';
import { SeededRandom } from './random';
import { typedArrayHash } from './contentHash';
import { dasin, datan2 } from './dmath';
import { cellCentre, cellOf, cellPoint, cellsNear } from './cells';
import { getSurfaceRadius } from './surface';
import { reliefWoods, standChance, RELIEF_WOODS, type ForestDensityMode, type ReliefWoods } from './forestDensity';
import {
  BIOMES, BIOME_MAX_SLOPE, DISC_ONLY, GROUND_PICKS, STAND_ANCHOR_MAX_SLOPE, biomesFor, contourHeading, layoutStand, offsetOnSphere,
  pick, slopeAt, standReach, tangentBasis,
} from './biomes';
import { biomeDressing } from './biomeDressing';
import { treeScale } from './treeScale';
import { climateCellAt, type ClimateCellId } from './climate';
import { climateWeights, climateTrees } from './biomeClimate';
import { WOODS, WOODS_STANDS, WOODS_RAINFOREST, isRainforest, standClimateAt, standFullness, standPacking, woodsRegimeOf, woodsStandAt, woodsDensityOf, type WoodsRegime } from './woodsDensity';
import { woodsTrailOf, onWoodsTrail, type WoodsTrail } from './woodsTrails';
import { drainageLinesOf, drainageSpansNear, drainageAtSpans, type DrainageLines } from './drainageLines';

export const FOREST = {
  /** A cell's side at a face's centre, in person-heights. */
  cellPeople: 40,
  /** Candidate stand anchors a cell draws (the stands' gap thins them to what fits: a greedy independent set). */
  anchorsPerCell: 8,
  /** The biome field's breadth: woods of one kind about this wide (person-heights). */
  regionPeople: 260,
  /** Where no biome's field reaches this, the ground is open (no stand). */
  openBelow: 0.62,
  /** Clear ground between two stands' rims (person-heights; dressBattleSite's `fits`). */
  standGapPeople: 4,
  /** Forest ids: idBase + cell × perCell + slot, inside 32 bits (propHash), above every dressed and stress id. */
  idBase: 134217728,   // 2^27
  perCell: 512,
} as const;

/** A forest tree's id: its cell, its stand's candidate (< 8) and its place in the stand's layout (< 64). */
export function forestTreeId(cell: number, k: number, i: number): number {
  return FOREST.idBase + cell * FOREST.perCell + k * 64 + i;
}
/** The cell and slot of a forest id (null: not a forest tree). */
export function forestIdCell(id: number): number | null {
  return id >= FOREST.idBase ? Math.floor((id - FOREST.idBase) / FOREST.perCell) : null;
}

/**
 * Decision 158 (the owner: "the planets"): a planet's forest seed — its archetype and a hash of its generated mesh
 * (`basePositions`, which only the generator writes: a crater changes the heights, not these). The same in every battle
 * on the planet (the sector carries its planets, a save regenerates them alike), so the same wood stands there and a
 * felled tree's id (forestTreeId) names the same tree in the next battle.
 */
export function planetForestSeed(planet: Pick<PlanetState, 'archetype' | 'basePositions'>): string {
  return `${planet.archetype}|${typedArrayHash(planet.basePositions).full.toString(36)}`;
}

/** The forest's cells per face edge on a planet (`person`: a person's height, world units). */
export function forestCellsPerEdge(planet: Pick<PlanetState, 'radius'>, person: number): number {
  return Math.max(8, Math.round(((Math.PI / 2) * planet.radius) / (FOREST.cellPeople * person)));
}

// ---- the biome field ----------------------------------------------------------------------------------------------

/** A 32-bit hash of a string (FNV-1a). */
function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}

/** A lattice point's value in [0, 1). */
function lattice(x: number, y: number, z: number, seed: number): number {
  let h = Math.imul(x, 0x8da6b343) ^ Math.imul(y, 0xd8163841) ^ Math.imul(z, 0xcb1ab31f) ^ seed;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

/** Smooth 3-D value noise in [0, 1) (trilinear over a smoothstep): arithmetic only, the same on every engine. */
function valueNoise(x: number, y: number, z: number, seed: number): number {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
  const fx = x - ix, fy = y - iy, fz = z - iz;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy), sz = fz * fz * (3 - 2 * fz);
  const c = (dx: number, dy: number, dz: number) => lattice(ix + dx, iy + dy, iz + dz, seed);
  const x00 = c(0, 0, 0) + (c(1, 0, 0) - c(0, 0, 0)) * sx, x10 = c(0, 1, 0) + (c(1, 1, 0) - c(0, 1, 0)) * sx;
  const x01 = c(0, 0, 1) + (c(1, 0, 1) - c(0, 0, 1)) * sx, x11 = c(0, 1, 1) + (c(1, 1, 1) - c(0, 1, 1)) * sx;
  const y0 = x00 + (x10 - x00) * sy, y1 = x01 + (x11 - x01) * sy;
  return y0 + (y1 - y0) * sz;
}

interface Field { channels: Array<{ biome: BiomeId; weight: number; seed: number }>; scale: number; byCell: Record<ClimateCellId, number[]> | null }
const fieldMemo = new WeakMap<object, Map<string, Field>>();

function fieldOf(planet: Pick<PlanetState, 'radius' | 'archetype' | 'palette'>, planetSeed: string, person: number, byClimate: boolean): Field {
  let bySeed = fieldMemo.get(planet);
  if (!bySeed) { bySeed = new Map(); fieldMemo.set(planet, bySeed); }
  const key = `${planetSeed}|${person}${byClimate ? '|climate' : ''}`;
  let f = bySeed.get(key);
  if (!f) {
    const list = biomesFor(planet), top = Math.max(1e-9, ...list.map(([, w]) => w));
    // (74.12 step 2: with the biomes by the climate, each cell's weights — the archetype's × the biome's affinity, the best kept at 1)
    f = { channels: list.map(([biome, w]) => ({ biome, weight: w / top, seed: hashString(`${planetSeed}|biome|${biome}`) })), scale: planet.radius / (FOREST.regionPeople * person), byCell: byClimate ? climateWeights(list) : null };
    bySeed.set(key, f);
  }
  return f;
}

/** The biome the field grows at a unit direction, or null: open ground (`fill`: the strongest channel whatever the open line — 393 (3) step 2's closed wood, where the density field says whether and the biome field only which). */
export function forestBiomeAt(planet: PlanetState, planetSeed: string, person: number, d: Vec3, byClimate = false, fill = false): BiomeId | null {
  const climate = byClimate && planet.heightmap && planet.heightmap.length > 0;
  const f = fieldOf(planet, planetSeed, person, climate);
  // 74.12 step 2 (core/biomeClimate.ts): the climate cell under the point reweights the channels — the kind by the climate, the blobs by the noise
  const weights = climate && f.byCell ? f.byCell[climateCellAt(planet, d)] : null;
  let best: BiomeId | null = null, bestScore: number = fill ? -Infinity : FOREST.openBelow;
  // (a biome the cell refuses — weight 0, savanna in the cold — keeps its blob but not its kind: it scores as the cell's
  // best-fitting biome and stands as that one, so the wooded share holds; a cell nothing fits is open)
  const top = weights ? weights.indexOf(1) : -1;
  for (let i = 0; i < f.channels.length; i++) {
    const ch = f.channels[i], w = weights ? weights[i] : ch.weight, refused = w <= 0;
    if (refused && top < 0) continue;
    const weight = refused ? 1 : w;
    // a weaker biome needs a stronger field to win (its weight scales the field's swing above the open line) — and, under the
    // line (426: the closed regime's fill, which takes the strongest channel whatever the line), its deficit is scaled the other
    // way, else a weak biome's shortfall shrank toward the line and the burn (weight 0.48, then 0.12) took the wet valleys' fill
    const v = valueNoise(d.x * f.scale, d.y * f.scale, d.z * f.scale, ch.seed);
    const score = FOREST.openBelow + (v - FOREST.openBelow) * (v >= FOREST.openBelow ? weight : 1 / Math.max(weight, 1e-3));
    if (score > bestScore) { bestScore = score; best = refused ? f.channels[top].biome : ch.biome; }
  }
  return best;
}

/** A stand's spacing: its biome's; under 'climate' the field's (standField). (67.61's jungle — a noise of its own that tightened a stand's spacing and doubled its trunks — is retired by 430: the rainforest is the closed regime's own packing with its own mix.) */
function standSpacing(planet: PlanetState, person: number, c: ForestCandidate, m: StandMemo): number {
  if (m.field) return standField(planet, person, c, m).spacing;
  return BIOMES[c.biome!].spacing;
}

// ---- the stands by the field (393 (3) step 2; core/woodsDensity.ts) ----------------------------------------------------

/** What the field makes of a stand: its density and fullness there (the biome's cold tolerance counted), the packing (standPacking), its spacing and radius, the warmth it grows in, and its regime (430: the rainforest's, warm and wet). */
interface StandField { density: number; fullness: number; packing: number; spacing: number; radius: number; warmth: number; riparian: number; exposure: number; regime: WoodsRegime }
function standField(planet: PlanetState, person: number, c: ForestCandidate, m: StandMemo): StandField {
  const key = slotOf(c), hit = m.field!.get(key);
  if (hit) return hit;
  const read = standClimateAt(planet, m.windSeed, person, c.dir, c.biome!), { warmth, riparian, exposure } = read;
  // (426: a lattice cell is closed as a whole — an anchor whose own read falls just under the threshold (the closed ground is a
  // thin band over it) takes the regime's packing, else half the cell's stands shrink and the wood breaks)
  const density = c.lattice ? Math.max(read.density, WOODS.closedFrom) : read.density;
  const fullness = standFullness(density), packing = standPacking(density);
  const def = BIOMES[c.biome!];
  // (426: the radius ramps to the closed regime's tiling disc, not down to a clump)
  const f: StandField = { density, fullness, packing, spacing: Math.max(WOODS_STANDS.spacingFloor, def.spacing * (1 - WOODS_STANDS.closedSpacing * packing)), radius: def.radius + (WOODS_STANDS.closedRadius - def.radius) * packing, warmth, riparian, exposure, regime: woodsRegimeOf(fullness, read.temperature, read.moisture) };
  m.field!.set(key, f);
  return f;
}
/**
 * 430: a stand's regime as its field reads it (a lattice cell closed as a whole; 'open' under 'relief' and 'flat', which have no
 * field) — the same read the stand's own trees took (447: the drawn layers and the depth field that asked it are gone).
 */
export function forestStandRead(planet: PlanetState, planetSeed: string, person: number, n: number, c: ForestCandidate, keepOut: readonly ForestKeepOut[] = NO_KEEP_OUT, density: ForestDensityMode = 'flat', windSeed = '', byClimate = false): { regime: WoodsRegime; radius: number } {
  const m = memoFor(planet, planetSeed, person, n, keepOut, density, windSeed, byClimate);
  if (!m.field || !c.biome) return { regime: 'open', radius: c.biome ? BIOMES[c.biome].radius : 0 };
  const f = standField(planet, person, c, m);
  return { regime: f.regime, radius: f.radius };
}
/**
 * A clearing in the closed wood: 426 (2), three seeded noises (WOODS_STANDS.clearings: each so many people across, its top
 * share — many small, few large) on the planet's seed; ground any one covers is a clearing (the jungle's stretch).
 */
const clearingSeeds = new Map<string, number[]>();
export function clearingAt(planet: Pick<PlanetState, 'radius'>, planetSeed: string, person: number, d: Vec3, only?: number): boolean {
  for (let i = 0; i < WOODS_STANDS.clearings.length; i++) {
    if (only !== undefined && i !== only) continue;   // (one scale alone: the measures)
    if (clearingNoiseAt(planet, planetSeed, person, d, i) > WOODS_STANDS.clearings[i][1]) return true;
  }
  return false;
}
/** Scale `i`'s clearing noise at `d`, in [0, 1) (above the scale's `above` it is a clearing); the measures read its quantiles to set the dials. */
export function clearingNoiseAt(planet: Pick<PlanetState, 'radius'>, planetSeed: string, person: number, d: Vec3, i: number): number {
  let seeds = clearingSeeds.get(planetSeed);
  if (seeds === undefined) { seeds = WOODS_STANDS.clearings.map(([people]) => hashString(`${planetSeed}|clearing|${people}`)); clearingSeeds.set(planetSeed, seeds); }
  const k = planet.radius / (WOODS_STANDS.clearings[i][0] * person);
  return valueNoise(d.x * k, d.y * k, d.z * k, seeds[i]);
}
/** 426 (2): the trails that may pass a cell — its own and its neighbours' within a trail's reach (core/woodsTrails.ts), memoised per cell. */
function trailsNear(planet: PlanetState, planetSeed: string, person: number, cell: number, m: StandMemo): WoodsTrail[] {
  const hit = m.trailsNear.get(cell);
  if (hit) return hit;
  const P = person / planet.radius, c = cellCentre(cell, m.n), l = Math.sqrt(c.x * c.x + c.y * c.y + c.z * c.z), d = { x: c.x / l, y: c.y / l, z: c.z / l };
  // (a trail starts at its cell's centre and runs trailPeople; a stand of this cell lies within a cell's half-diagonal of its centre, its trees within the closed disc and a tree's slack)
  const reach = (WOODS_STANDS.trailPeople + WOODS_STANDS.closedRadius + 2) * P + cellArc(m.n);
  const out: WoodsTrail[] = [];
  for (const q of cellsNear(d, reach, m.n)) {
    let t = m.trails.get(q);
    if (t === undefined) { t = woodsTrailOf(planet, planetSeed, m.windSeed, person, m.n, q); m.trails.set(q, t); }
    if (t) out.push(t);
  }
  m.trailsNear.set(cell, out);
  return out;
}
/** A cell's half-diagonal as an angle (the cube face's cell on the sphere, roughly). */
function cellArc(n: number): number {
  return Math.sqrt(2) * (Math.PI / 4) / n;
}
/** The wood's edge at a stand: the heading along the field's contour (across its gradient, read edgeSamplePeople either side), or null where the field is flat. */
function edgeHeading(planet: PlanetState, windSeed: string, person: number, d: Vec3, t1: Vec3, t2: Vec3): number | null {
  const P = person / planet.radius, g = WOODS_STANDS.edgeSamplePeople;
  const at = (x: number, y: number) => woodsStandAt(planet, windSeed, person, offsetOnSphere(d, t1, t2, x, y, P));
  const gx = at(g, 0) - at(-g, 0), gy = at(0, g) - at(0, -g);
  if (gx * gx + gy * gy < 1e-4) return null;
  return datan2(gy, gx) + Math.PI / 2;
}
/** The treeline's trees: krummholz, a few of the biome's own among them. */
const TREELINE_KRUMMHOLZ = 3, TREELINE_OWN = 0.3;
/** 426 (2), the creek cut: the bank mix's tree at a unit draw `u` (WOODS_STANDS.bankTrees, by weight). */
function bankTree(u: number): FloraState['floraType'] {
  const rows = WOODS_STANDS.bankTrees;
  let acc = u * rows.reduce((a, [, w]) => a + w, 0);
  for (const [t, w] of rows) { acc -= w; if (acc <= 0) return t; }
  return rows[rows.length - 1][0];
}
/** Step 3, the windward flank: a stand's mix with snags added by the exposure (`WOODS_STANDS.windwardSnags` × exposure of the trees at full exposure half). */
export function windwardMix(own: Array<[FloraState['floraType'], number]>, exposure: number): Array<[FloraState['floraType'], number]> {
  if (!(exposure > 0)) return own;
  const total = own.reduce((a, [, w]) => a + w, 0), share = Math.min(1, WOODS_STANDS.windwardSnags * exposure);
  if (!(total > 0) || share <= 0) return own;
  // (the snags' weight so that they are `share` of the draw: share / (1 − share) of the rest)
  return [['snag', total * share / (1 - share)], ...own];
}

// ---- stands --------------------------------------------------------------------------------------------------------

/** A stand's candidate anchor: where it is, and the biome the field grows there (null: open ground, no stand). */
export interface ForestCandidate { cell: number; k: number; dir: Vec3; biome: BiomeId | null; /** 426: an anchor of a closed cell's lattice — the cell is closed as a whole: full packing, no clearing refusal (its clearings cut trees) */ lattice?: true }

const candidateMemo = new WeakMap<object, Map<string, ForestCandidate[]>>();

/**
 * A cell's candidate anchors: FOREST.anchorsPerCell points in the cell from its own stream; a biome that seeks low or
 * high ground takes the lowest or highest of GROUND_PICKS draws in its own biome (dressBattleSite's ground pick).
 * `fill` (393 (3) step 2, the wind seed under the density 'climate'): where the noise leaves the ground open but the stands'
 * read of the woods density field (step 3: the riparian floor in) reads the wood's edge or past it (WOODS_STANDS.edgeFrom),
 * the candidate takes the strongest biome anyway — the closed wood and the river's belt are continuous; the parkland keeps
 * the noise's patches.
 */
export function forestCandidates(planet: PlanetState, planetSeed: string, person: number, n: number, cell: number, byClimate = false, fill: string | null = null): ForestCandidate[] {
  let byKey = candidateMemo.get(planet);
  if (!byKey) { byKey = new Map(); candidateMemo.set(planet, byKey); }
  const key = `${planetSeed}|${person}|${n}|${cell}${byClimate ? '|climate' : ''}${fill !== null ? `|fill|${fill}` : ''}`;
  const kept = byKey.get(key);
  if (kept) return kept;
  const rng = new SeededRandom(`${planetSeed}|forest|${cell}|a`);
  const out: ForestCandidate[] = [];
  // 426 (continuity), the climate arm: a cell whose field reads closed at its centre lays its anchors on a jittered lattice — a
  // 3 × 3 grid of the cell less one slot, each anchor jittered within its own square — so the stands' discs tile the cell and
  // the wood is continuous; the random draws, the ground pick and the fill stay the parkland's (and the relief arm's: no fill)
  if (fill !== null && standFullness(woodsStandAt(planet, fill, person, cellCentre(cell, n))) >= 1) {
    const side = Math.ceil(Math.sqrt(FOREST.anchorsPerCell)), slots = side * side, drop = rng.int(0, slots - 1), jitter = WOODS_STANDS.latticeJitter / side;
    for (let s = 0, k = 0; s < slots && k < FOREST.anchorsPerCell; s++) {
      if (s === drop) continue;
      const i = s % side, j = (s - i) / side;
      // (GROUND_PICKS jittered draws in the slot's square; the first unless it is too steep to root, then the flattest — a stand
      // refused for its anchor's slope would leave a 26-people hole in the wood, so on hilly ground it roots on the flattest spot near)
      const draws: Vec3[] = [];
      for (let g = 0; g < GROUND_PICKS; g++) draws.push(cellPoint(cell, n, (i + 0.5) / side + (rng.range(0, 1) - 0.5) * jitter, (j + 0.5) / side + (rng.range(0, 1) - 0.5) * jitter));
      let dir = draws[0];
      if (slopeAt(planet, dir, 2 * person) > STAND_ANCHOR_MAX_SLOPE) { let best = Infinity; for (const d of draws) { const sl = slopeAt(planet, d, 2 * person); if (sl < best) { best = sl; dir = d; } } }
      // (the cell is closed as a whole: the fill's biome on every anchor the noise leaves open, whatever its own read)
      const biome = forestBiomeAt(planet, planetSeed, person, dir, byClimate) ?? forestBiomeAt(planet, planetSeed, person, dir, byClimate, true);
      out.push({ cell, k: k++, dir, biome, lattice: true });
    }
    byKey.set(key, out);
    return out;
  }
  for (let k = 0; k < FOREST.anchorsPerCell; k++) {
    const draws: Vec3[] = [];
    for (let g = 0; g < GROUND_PICKS; g++) draws.push(cellPoint(cell, n, rng.range(0, 1), rng.range(0, 1)));
    let biome = forestBiomeAt(planet, planetSeed, person, draws[0], byClimate);
    if (!biome && fill !== null && standFullness(woodsStandAt(planet, fill, person, draws[0])) >= WOODS_STANDS.edgeFrom) biome = forestBiomeAt(planet, planetSeed, person, draws[0], byClimate, true);
    let dir = draws[0];
    const pref = biome ? BIOMES[biome].ground ?? 'any' : 'any';
    if (pref !== 'any') {
      let bestH = getSurfaceRadius(planet, dir);
      for (let g = 1; g < GROUND_PICKS; g++) {
        if (forestBiomeAt(planet, planetSeed, person, draws[g], byClimate) !== biome) continue;
        const h = getSurfaceRadius(planet, draws[g]);
        if (pref === 'low' ? h < bestH : h > bestH) { dir = draws[g]; bestH = h; }
      }
    }
    out.push({ cell, k, dir, biome });
  }
  byKey.set(key, out);
  return out;
}

/** A disc on the ground the forest keeps out of (the battle's own dressed area, a base): a direction and an angle. */
export interface ForestKeepOut { dir: Vec3; angle: number }

/** A candidate's rank: a hash of its cell and slot, so who yields to whom is spread evenly (no long chains). */
function rankOf(c: ForestCandidate, seed: number): number {
  return lattice(c.cell, c.k, 0x5f3759df, seed);
}

interface StandMemo { kept: Map<number, boolean>; trees: Map<number, FloraState[]>; cells: Map<number, FloraState[]>; seed: number; density: ForestDensityMode; relief: ReliefWoods | null; byClimate: boolean; windSeed: string; field: Map<number, StandField> | null; /** 426 (2): cells per edge, each cell's trail (null: none) and the trails near each cell */ n: number; trails: Map<number, WoodsTrail | null>; trailsNear: Map<number, WoodsTrail[]>; /** 426 (2), the creek cut: the drainage lines (74.20) under 'climate', else null */ drainage: DrainageLines | null }
const standMemo = new WeakMap<object, Map<string, StandMemo>>();
const NO_KEEP_OUT: readonly ForestKeepOut[] = [];
const keepOutIds = new WeakMap<readonly ForestKeepOut[], number>();
let keepOutNext = 1;

function memoFor(planet: PlanetState, planetSeed: string, person: number, n: number, keepOut: readonly ForestKeepOut[], density: ForestDensityMode = 'flat', windSeed = '', byClimate = false): StandMemo {
  let byKey = standMemo.get(planet);
  if (!byKey) { byKey = new Map(); standMemo.set(planet, byKey); }
  let kid = keepOutIds.get(keepOut);
  if (kid === undefined) { kid = keepOut.length ? keepOutNext++ : 0; keepOutIds.set(keepOut, kid); }
  const key = `${planetSeed}|${person}|${n}|${kid}${density === 'flat' ? '' : `|${density}|${windSeed}`}${byClimate ? '|climate' : ''}`;
  let m = byKey.get(key);
  if (!m) {
    // (393 (3) step 2: under 'climate' the relief is one factor of the field — built here too, with the field)
    m = { kept: new Map(), trees: new Map(), cells: new Map(), seed: hashString(`${planetSeed}|rank`), density, relief: density !== 'flat' ? reliefWoods(planet, windSeed, person) : null, byClimate, windSeed, field: density === 'climate' ? new Map() : null, n, trails: new Map(), trailsNear: new Map(), drainage: density === 'climate' ? drainageLinesOf(planet, person) : null };
    if (m.field) woodsDensityOf(planet, windSeed, person);
    byKey.set(key, m);
  }
  return m;
}

const slotOf = (c: ForestCandidate) => c.cell * 8 + c.k;
/** Whether q outranks c (a strict order: the hashed rank, then the slot). */
function outranks(q: ForestCandidate, c: ForestCandidate, seed: number): boolean {
  const rq = rankOf(q, seed), rc = rankOf(c, seed);
  return rq > rc || (rq === rc && slotOf(q) > slotOf(c));
}

/**
 * Whether a candidate is a stand: it grows a biome, roots, clears the keep-out, and no candidate that outranks it and
 * stands within the two stands' radii and the gap is a stand itself — a greedy independent set under a hashed rank:
 * local, deterministic, as close-packed as dressBattleSite's stands (its `fits`: radius + radius + 4 people; a scatter's
 * or a belt's outer trees may reach into a neighbour's, and give way tree by tree in forestCell). Memoised per planet
 * object, seed, grid and keep-out (a crater's new planet starts afresh).
 */
function isStand(planet: PlanetState, planetSeed: string, person: number, n: number, c: ForestCandidate, keepOut: readonly ForestKeepOut[], m: StandMemo): boolean {
  const key = slotOf(c);
  const known = m.kept.get(key);
  if (known !== undefined) return known;
  const P = person / planet.radius;
  let ok = !!c.biome && slopeAt(planet, c.dir, 2 * person) <= STAND_ANCHOR_MAX_SLOPE;
  const r = c.biome ? BIOMES[c.biome].radius * P : 0;
  if (ok) ok = !keepOut.some(o => angleBetween(c.dir, o.dir) < o.angle + r);
  // E5.4's density by relief (core/forestDensity.ts; off: 'flat'): the stand roots by its chance there — a draw of its own,
  // before the neighbours' test, so a candidate the ground thins blocks no other — and in the dense core packs closer
  let gap = FOREST.standGapPeople * P, shrink = 1, rc = r;
  if (ok && m.field && c.biome) {
    // 393 (3) step 2: the stand's chance, gap, radius and exclusion by the field at its anchor (standField); in the closed
    // regime (fullness 1) the gap is 0 and the small dense clumps overlap and merge — a continuous canopy — less its clearings
    const f = standField(planet, person, c, m);
    const chance = (RELIEF_WOODS.ceiling[c.biome] ?? 1) * (WOODS_STANDS.chanceFloor + (1 - WOODS_STANDS.chanceFloor) * f.fullness);
    // (426: a lattice stand is not refused by the clearing noise — a refused lattice stand is a 26-people hole; its clearing cuts trees, standTrees)
    ok = new SeededRandom(`${planetSeed}|dense|${c.cell}|${c.k}`).next() < chance && !(f.fullness >= 1 && !c.lattice && clearingAt(planet, planetSeed, person, c.dir));
    gap *= 1 - f.packing; shrink = 1 - WOODS_STANDS.overlap * f.packing; rc = f.radius * P;
  } else if (ok && m.relief && c.biome) {
    const chance = standChance(planet, m.relief, c.dir, c.biome);
    ok = new SeededRandom(`${planetSeed}|dense|${c.cell}|${c.k}`).next() < chance;
    if (chance / Math.max(1e-9, RELIEF_WOODS.ceiling[c.biome] ?? 1) >= RELIEF_WOODS.denseFrom) gap *= RELIEF_WOODS.denseGap;
  }
  if (ok) {
    outer: for (const other of cellsNear(c.dir, r + gap + MAX_RADIUS_PEOPLE * P, n)) {
      for (const q of forestCandidates(planet, planetSeed, person, n, other, m.byClimate, m.field ? m.windSeed : null)) {
        if (!q.biome || slotOf(q) === key || !outranks(q, c, m.seed)) continue;
        if (angleBetween(c.dir, q.dir) >= (rc + (m.field ? standField(planet, person, q, m).radius : BIOMES[q.biome].radius) * P) * shrink + gap) continue;
        if (isStand(planet, planetSeed, person, n, q, keepOut, m)) { ok = false; break outer; }
      }
    }
  }
  m.kept.set(key, ok);
  return ok;
}

/** The stands of a cell: its candidates that are stands (see isStand), in slot order. */
export function forestStands(planet: PlanetState, planetSeed: string, person: number, n: number, cell: number, keepOut: readonly ForestKeepOut[] = NO_KEEP_OUT, density: ForestDensityMode = 'flat', windSeed = '', byClimate = false): ForestCandidate[] {
  const m = memoFor(planet, planetSeed, person, n, keepOut, density, windSeed, byClimate);
  return forestCandidates(planet, planetSeed, person, n, cell, byClimate, m.field ? m.windSeed : null).filter(c => isStand(planet, planetSeed, person, n, c, keepOut, m));
}

/** The angle between two unit directions (from the chord: dmath, the same on every engine). */
function angleBetween(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
  return 2 * dasin(Math.min(1, Math.sqrt(dx * dx + dy * dy + dz * dz) / 2));
}

/**
 * A stand's trees as laid out: its biome's shape, count and spacing (stream `…|<k>`), each tree's draws made before its
 * slope and keep-out tests, so a dropped tree moves no other. Before any neighbour gives way (see forestCell).
 */
function standTrees(planet: PlanetState, planetIndex: number, planetSeed: string, person: number, s: ForestCandidate, keepOut: readonly ForestKeepOut[], m: StandMemo): FloraState[] {
  const key = slotOf(s), kept = m.trees.get(key);
  if (kept) return kept;
  const P = person / planet.radius, def = BIOMES[s.biome!], out: FloraState[] = [];
  const rng = new SeededRandom(`${planetSeed}|forest|${s.cell}|${s.k}`);
  let shape = pick(def.shapes ?? DISC_ONLY, rng);
  let heading = shape === 'contour' ? contourHeading(planet, s.dir, 2 * person) : shape === 'belt' ? rng.range(0, Math.PI) : 0;
  const [t1, t2] = tangentBasis(s.dir);
  // (393 (3) step 2, the arrangement: in the closed regime the shapes collapse to the disc; at the wood's edge a disc or a
  // scatter is a belt along the field's contour)
  const f = m.field ? standField(planet, person, s, m) : null;
  if (f && f.fullness >= 1) { shape = 'disc'; heading = 0; }
  else if (f && f.riparian > 0 && shape !== 'contour') { shape = 'contour'; heading = contourHeading(planet, s.dir, 2 * person); }   // (step 3: on a bank the stand lies along it)
  else if (f && f.fullness >= WOODS_STANDS.edgeFrom && (shape === 'disc' || shape === 'scatter')) {
    const h = edgeHeading(planet, m.windSeed, person, s.dir, t1, t2);
    if (h !== null) { shape = 'belt'; heading = h; }
  }
  // (E5.4's density by relief: in the dense core more trees, tighter — the count drawn first, as ever, so the draws keep their order)
  const count = rng.int(def.stand[0], def.stand[1]);
  const dense = !f && !!m.relief && standChance(planet, m.relief, s.dir, s.biome!) / Math.max(1e-9, RELIEF_WOODS.ceiling[s.biome!] ?? 1) >= RELIEF_WOODS.denseFrom;
  const layout = f
    ? layoutStand(shape, Math.min(64, Math.round(count * (1 + WOODS_STANDS.closedTrees * f.packing))), f.radius, f.spacing, rng, heading)
    : layoutStand(shape, dense ? Math.min(64, Math.round(count * RELIEF_WOODS.denseTrees)) : count, def.radius, dense ? def.spacing * RELIEF_WOODS.denseSpacing : def.spacing, rng, heading);
  // (430: in the rainforest regime and at its edge the stand draws the rainforest's mix instead of its biome's — the same draws,
  // the same count: the regime adds no stems; the treeline never reaches it (it is warm) and the windward snags still do)
  const own: Array<[FloraState['floraType'], number]> = f && isRainforest(f.regime) ? WOODS_RAINFOREST.trees.map(([t, w]) => [t, w]) : biomeDressing(s.biome!).trees;
  // (393 (3) step 2, the treeline: under the warmth curve's floor the stand is krummholz with a few of its own)
  const treeline = f && f.warmth < WOODS_STANDS.treelineBelow ? [['krummholz', TREELINE_KRUMMHOLZ] as [FloraState['floraType'], number], ...own.map(([t, w]) => [t, w * TREELINE_OWN] as [FloraState['floraType'], number])] : own;
  const mix = f ? windwardMix(treeline, f.exposure) : treeline;   // (step 3: the windward flank's snags)
  // (426 (2): a trail cuts the trees of every stand in the closed regime — a lattice stand's, and a closed stand the parkland's draw laid at the wood's edge)
  const trails = s.lattice || (f && f.fullness >= 1) ? trailsNear(planet, planetSeed, person, s.cell, m) : null;
  // (426 (2), the creek cut: the spans within the stand's reach, read once at its anchor — a tree lies within the reach of
  // its anchor, so every span its own read would find is here; most stands find none and skip the per-tree read, which as
  // drainageAt's bucket walk a tree (67 spans, 2.65 µs) cost 2–2.7 ms a crossed cell)
  const creeks = m.drainage ? drainageSpansNear(planet, m.drainage, s.dir, Math.max(f ? f.radius : def.radius, forestStandReach(s.biome!)) + 1) : null;
  layout.forEach((o, i) => {
    // (383: by the climate, the glowing kinds thinned outside a cold cell)
    let floraType = pick(m.byClimate ? climateTrees(mix, climateCellAt(planet, s.dir)) : mix, rng);
    const scale = treeScale(floraType, rng);   // (70.5: the biome's dressing; its trees by material wait for 74.3's field — the forest is pure of the ground's digging)
    if (i >= 64) return;
    const d = offsetOnSphere(s.dir, t1, t2, o.x, o.y, P);
    if (slopeAt(planet, d, 2 * person) > BIOME_MAX_SLOPE) return;
    if (s.lattice && clearingAt(planet, planetSeed, person, d)) return;   // (426: the closed wood's clearings, cut from the trees)
    if (trails && onWoodsTrail(d, trails, WOODS_STANDS.trailWidth / 2 * P)) return;   // (426 (2): the game trails, the trees giving way)
    if (creeks && creeks.length) {
      // (426 (2), the creek cut on 74.20's drainage lines: no trunk in a bed; on a damp bed's bank the bank mix — the kind
      // by a hash of the tree's id, not a draw, so the stream and every other tree stay as they were)
      const creek = drainageAtSpans(m.drainage!, d, creeks);
      if (creek) {
        if (creek.edge <= 0) return;
        if (creek.kind === 'damp') floraType = bankTree(lattice(s.cell, s.k, i, m.seed));
      }
    }
    if (keepOut.some(k => angleBetween(d, k.dir) < k.angle)) return;
    out.push({ id: forestTreeId(s.cell, s.k, i), floraType, planetIndex, surfaceDir: d, alive: true, scale, size: 'normal', biome: s.biome! });
  });
  m.trees.set(key, out);
  return out;
}

/**
 * The trees of one cell on `planet` (index `planetIndex` in the state): its stands' trees, less any that crowds a tree
 * of an outranking stand (closer than 0.85 of the smaller spacing, as dressBattleSite's trees give way). Pure: the same
 * arguments give the same trees, in the same order, with the same ids.
 */
export function forestCell(planet: PlanetState, planetIndex: number, planetSeed: string, person: number, n: number, cell: number, keepOut: readonly ForestKeepOut[] = NO_KEEP_OUT, density: ForestDensityMode = 'flat', windSeed = '', byClimate = false): FloraState[] {
  const m = memoFor(planet, planetSeed, person, n, keepOut, density, windSeed, byClimate);
  // (393 (3): the cell's trees memoised with the stands' — the held band asks for a wanted cell's trees every band tick while
  // its cap is full (the cell waits), and the crowding below, over every rival stand's trees, cost a third of a millisecond a
  // tick in a closed wood on the megaplanet Story. The caller gets its own copies, as before, stamped with its planetIndex: the
  // memo is per planet object and its key does not name the index, so nothing index-bound is read from it. Pure still.)
  let cached = m.cells.get(cell);
  if (!cached) {
    cached = forestCellTrees(planet, planetIndex, planetSeed, person, n, cell, keepOut, m);
    if (m.cells.size >= CELL_MEMO_MAX) m.cells.delete(m.cells.keys().next().value!);
    m.cells.set(cell, cached);
  }
  return cached.map(t => ({ ...t, planetIndex, surfaceDir: { ...t.surfaceDir } }));
}

/** The most cells' trees kept in a memo (the oldest built forgotten first; a megaplanet Story holds ~200 cells and visits ~800). */
const CELL_MEMO_MAX = 4096;

function forestCellTrees(planet: PlanetState, planetIndex: number, planetSeed: string, person: number, n: number, cell: number, keepOut: readonly ForestKeepOut[], m: StandMemo): FloraState[] {
  const P = person / planet.radius, out: FloraState[] = [], { density, windSeed, byClimate } = m;
  for (const s of forestStands(planet, planetSeed, person, n, cell, keepOut, density, windSeed, byClimate)) {
    const reach = forestStandReach(s.biome!) * P;
    // the stands that outrank this one and reach into it
    const rivals: Array<{ trees: FloraState[]; spacing: number }> = [];
    for (const other of cellsNear(s.dir, reach + MAX_REACH_PEOPLE * P, n)) {
      for (const q of forestCandidates(planet, planetSeed, person, n, other, m.byClimate, m.field ? m.windSeed : null)) {
        if (!q.biome || slotOf(q) === slotOf(s) || !outranks(q, s, m.seed)) continue;
        if (angleBetween(s.dir, q.dir) >= reach + forestStandReach(q.biome) * P) continue;
        if (!isStand(planet, planetSeed, person, n, q, keepOut, m)) continue;
        rivals.push({ trees: standTrees(planet, planetIndex, planetSeed, person, q, keepOut, m), spacing: standSpacing(planet, person, q, m) });
      }
    }
    const mine = standSpacing(planet, person, s, m);
    for (const t of standTrees(planet, planetIndex, planetSeed, person, s, keepOut, m)) {
      const crowded = rivals.some(r => r.trees.some(u => angleBetween(t.surfaceDir, u.surfaceDir) < 0.85 * Math.min(mine, r.spacing) * P));
      if (!crowded) out.push(t);
    }
  }
  return out;
}

/**
 * E5.4 step 4: the ground the forest is placed on — the planet with its heightmap at setup (`basis`), the same object
 * while it is (so the memos hold), whatever craters have done since: a cell made later is the cell made earlier.
 */
const basisMemo = new WeakMap<Float32Array, PlanetState>();
export function basisPlanet(planet: PlanetState, basis: Float32Array): PlanetState {
  let b = basisMemo.get(basis);
  if (!b) { b = { ...planet, heightmap: basis }; basisMemo.set(basis, b); }
  return b;
}

/**
 * E5.4: where the forest keeps out of on a planet, as at setup — the battle's own dressed areas (GameState.dressedAreas)
 * and every structure's ground (6 people round it, and a stand's radius and the gap past that). The drawing and the sim
 * read the same discs (core/forestHeld.ts keeps them in the state).
 */
export function forestKeepOutAt(state: Pick<GameState, 'planets' | 'structures'> & Partial<Pick<GameState, 'dressedAreas'>>, planetIndex: number, person: number): ForestKeepOut[] {
  const planet = state.planets[planetIndex];
  if (!planet) return [];
  const P = person / planet.radius, margin = (Math.max(...Object.values(BIOMES).map(b => b.radius)) + FOREST.standGapPeople) * P;
  const discs: ForestKeepOut[] = (state.dressedAreas ?? []).filter(a => a.planetIndex === planetIndex).map(a => ({ dir: a.dir, angle: a.angle }));
  for (const st of state.structures ?? []) {
    if (st.planetIndex !== planetIndex) continue;
    const l = Math.sqrt(st.surfaceDir.x * st.surfaceDir.x + st.surfaceDir.y * st.surfaceDir.y + st.surfaceDir.z * st.surfaceDir.z) || 1;
    discs.push({ dir: { x: st.surfaceDir.x / l, y: st.surfaceDir.y / l, z: st.surfaceDir.z / l }, angle: 6 * P + margin });
  }
  return discs;
}

/** The forest cell a direction falls in. */
export function forestCellOf(d: Vec3, n: number): number { return cellOf(d.x, d.y, d.z, n); }

/** The widest stand radius and reach of any biome (person-heights): how far a search for rivals looks. */
const MAX_RADIUS_PEOPLE = Math.max(...Object.values(BIOMES).map(b => b.radius));
const MAX_REACH_PEOPLE = Math.max(...(Object.keys(BIOMES) as BiomeId[]).map(b => forestStandReach(b)));

/** A stand's reach in person-heights: its biome's radius × the widest of the shapes it takes (a belt, a scatter reach past it). */
export function forestStandReach(biome: BiomeId): number {
  const def = BIOMES[biome];
  return def.radius * Math.max(...(def.shapes ?? DISC_ONLY).map(([shape]) => standReach(shape)));
}
